// Segmented (audit §4b): one frame divided by hairlines, the segment that's on filled in ink
// (DESIGN.md §8). Two kinds, one drawing:
// - a choice of one (the row switch: comfortable / compact): selectionMode 'single', a radio-like group;
// - a choice of several (the working days, the ways to sign in): selectionMode 'multiple', each
//   segment a toggle that's pressed or not;
// - a row of actions (‹ Today ›): selectionMode 'none', each segment a button, and `on` marks the one
//   that describes what's shown (Today, when the board shows today).
// A segment is text, or a glyph with a label for screen readers. A segment may carry a count after its
// words (Messages' "Couldn’t send 3"), muted, or red when it's a problem; it's said with its words
// ("Couldn’t send, 3 couldn’t be sent"). Five or so that must all stay in view on a phone (Messages'
// statuses) take `wrap`: on a narrow screen each segment is then a framed pill of its own, wrapping onto
// as many rows as it needs, never a frame wider than the screen. Reads only --seg-*.
import type { Key } from 'react-aria-components';
import { Button as AriaButton, ToggleButton, ToggleButtonGroup } from 'react-aria-components';
import { Glyph, type GlyphName } from '../Glyph/Glyph.tsx';
import './Segmented.css';

export interface Segment {
  id: string;
  /** The words, or the screen-reader label when the segment is a glyph. */
  label: string;
  icon?: GlyphName;
  isDisabled?: boolean;
  /** How many, after the words ("Replies 2"); none when it's 0. */
  count?: number;
  /** bad: the count is red (what couldn't be sent). */
  countTone?: 'bad';
  /** The count for screen readers ("3 couldn’t be sent"); "<count>" when left out. */
  countLabel?: string;
}

interface Common {
  /** What the group is, for screen readers ("Rows", "Day"). */
  label: string;
  items: Segment[];
  /** On a phone (560 or narrower), each segment its own pill, wrapping: every one in view. */
  wrap?: boolean;
}
export type SegmentedProps =
  | (Common & {
      selectionMode?: 'single';
      selectedKey: string;
      onSelectionChange?: (id: string) => void;
    })
  | (Common & {
      selectionMode: 'multiple';
      selectedKeys: readonly string[];
      onSelectionChange?: (ids: string[]) => void;
    })
  | (Common & {
      selectionMode: 'none';
      onAction?: (id: string) => void;
      /** The segment that describes what's shown, drawn on (Today). */
      on?: string;
    });

function Face({ item }: { item: Segment }) {
  if (item.icon) return <Glyph name={item.icon} className="fl-seg__glyph" />;
  const count = item.count ? (item.count > 99 ? '99+' : String(item.count)) : null;
  return (
    <>
      <span className="fl-seg__label">{item.label}</span>
      {count ? (
        <span className="fl-seg__count" data-tone={item.countTone} aria-hidden="true">
          {count}
        </span>
      ) : null}
    </>
  );
}

/** A segment's name for screen readers: a glyph's label, or its words and its count ("Couldn’t send, 3
 * couldn’t be sent"). */
const nameOf = (item: Segment) =>
  item.icon
    ? item.label
    : item.count
      ? `${item.label}, ${item.countLabel ?? (item.count > 99 ? '99+' : item.count)}`
      : undefined;

export function Segmented(props: SegmentedProps) {
  if (props.selectionMode === 'none') {
    const { label, items, onAction, on } = props;
    return (
      <div role="group" aria-label={label} className="fl-seg" data-wrap={props.wrap ? '' : undefined}>
        {items.map((item) => (
          <AriaButton
            key={item.id}
            className="fl-seg__item"
            data-icon={item.icon ? '' : undefined}
            data-on={on === item.id ? '' : undefined}
            aria-current={on === item.id ? 'true' : undefined}
            aria-label={nameOf(item)}
            isDisabled={item.isDisabled}
            onPress={() => onAction?.(item.id)}
          >
            <Face item={item} />
          </AriaButton>
        ))}
      </div>
    );
  }
  const toggles = props.items.map((item) => (
    <ToggleButton
      key={item.id}
      id={item.id}
      className="fl-seg__item"
      data-icon={item.icon ? '' : undefined}
      aria-label={nameOf(item)}
      isDisabled={item.isDisabled}
    >
      <Face item={item} />
    </ToggleButton>
  ));
  if (props.selectionMode === 'multiple') {
    const { label, selectedKeys, onSelectionChange } = props;
    return (
      <ToggleButtonGroup
        aria-label={label}
        className="fl-seg"
        data-wrap={props.wrap ? '' : undefined}
        selectionMode="multiple"
        selectedKeys={selectedKeys}
        onSelectionChange={(keys: Set<Key>) => onSelectionChange?.([...keys].map(String))}
      >
        {toggles}
      </ToggleButtonGroup>
    );
  }
  const { label, selectedKey, onSelectionChange } = props;
  return (
    <ToggleButtonGroup
      aria-label={label}
      className="fl-seg"
      data-wrap={props.wrap ? '' : undefined}
      selectionMode="single"
      disallowEmptySelection
      selectedKeys={[selectedKey]}
      onSelectionChange={(keys: Set<Key>) => {
        const [k] = [...keys];
        if (k !== undefined) onSelectionChange?.(String(k));
      }}
    >
      {toggles}
    </ToggleButtonGroup>
  );
}
